<template>
    <div class="container">
        <div v-if="hasFeature('feat_maintenance_requests') && !loading" class="mt-5">
            <div class="bg-white rounded-lg shadow-md p-8 gap-y-6 mb-10">
                <div class="flex flex-col gap-4">
                    <!-- Logo -->
                    <div class="mb-2 p-3 text-center">
                        <image-upload :upload-url="logoUploadUrl" :default-image-url="form.logo_url"></image-upload>
                    </div>
                    <!-- Slug -->
                    <div class="form-group">
                        <div class="m-2 text-center">
                            <div class="flex justify-center">
                                <a :href="form.public_page_url" target="_blank"
                                   class="py-2 px-3 font-weight-bold cursor-pointer" style="background-color: #efefef; border-radius: 4px 0 0 4px"
                                   :style="'color:' + form.page_branding_primary_color">
                                    {{ form.public_page_url }}
                                </a>

                                <button class="btn btn-primary outline-none"
                                        style="border-radius: 0 4px 4px 0"
                                        :style="'background:' + form.page_branding_primary_color + '; border-color:' + form.page_branding_primary_color"
                                        @click.prevent="copyPageUrl">
                                    Copy to clipboard
                                </button>

                                <button class="btn btn-primary ml-2 outline-none" v-if="app.state.user.support"
                                        @click="showChangeSlugModal = true; form.setErrors(null)">Change
                                </button>
                            </div>

                            <p class="mx-auto mt-2" style="max-width: 400px">
                                Share this link with your reporters to have them report problems directly to your RedRabbit.
                            </p>
                        </div>
                    </div>

                    <div class="grid grid-cols-2 gap-y-4 gap-x-6">
                        <!-- Primary Branding Color -->
                        <div class="col-span-2 sm:col-span-1 flex items-center p-2" style="border: 2px solid; border-radius: 4px;"
                             :style="'border-color:' + form.page_branding_primary_color">
                            <el-color-picker v-model="form.page_branding_primary_color"></el-color-picker>
                            <span class="label" :style="'color:' + form.page_branding_primary_color"><span class="font-medium text-md">Primary Branding Color</span>
                                <br/> Select the branding color to use for your maintenance request page.
                            </span>
                        </div>

                        <!-- Require Reference Field -->
                        <div class="col-span-2 sm:col-span-1 text-md text-gray-800 font-medium my-auto">
                            <checkbox
                                v-model="form.require_reference" label="Require Reference Field"
                                class="flex-grow-1 justify-content-start font-medium">
                            </checkbox>

                            <div class="text-sm text-gray-500 font-normal">
                                Enable this to require reporters to provide a property or payment reference when submitting a maintenance request.
                            </div>
                        </div>

                        <!-- Info Message -->
                        <div class="col-span-2 text-md text-gray-800 font-medium">
                            Informational Message
                            <div class="text-sm text-gray-500 font-normal">
                                This message will be displayed on your maintenance requests page. Use it to provide important information to reporters when they are logging issues.
                            </div>

                            <textarea type="text" class="form-control mt-2"
                                      v-model="form.info_message"
                                      rows="2"
                                      :class="{'is-invalid': form.errors.has('info_message')}"></textarea>
                            <span class="invalid-feedback" v-show="form.errors.has('info_message')">
                                {{ form.errors.get("info_message") }}
                            </span>
                        </div>

                        <!-- Success Message -->
                        <div class="col-span-2 text-md text-gray-800 font-medium">
                            Success Message
                            <div class="text-sm text-gray-500 font-normal">
                                This message will be displayed when a reporter successfully reports an issue. Use it to provide typical issue resolution timelines, etc.
                            </div>

                            <textarea type="text" class="form-control mt-2"
                                      v-model="form.success_message"
                                      rows="2"
                                      :class="{'is-invalid': form.errors.has('success_message')}"></textarea>
                            <span class="invalid-feedback" v-show="form.errors.has('success_message')">
                                {{ form.errors.get("success_message") }}
                            </span>
                        </div>

                        <!-- Success Action -->
                        <div class="col-span-1 text-md text-gray-800 font-medium">
                            Success Action
                            <div class="text-sm text-gray-500 font-normal">
                                What action do you want to provide to the reporter after reporting an issue
                            </div>

                            <input type="text" class="form-control mt-2"
                                   v-model="form.success_action"
                                   :class="{'is-invalid': form.errors.has('success_action')}"></input>
                            <span class="invalid-feedback" v-show="form.errors.has('success_action')">
                                {{ form.errors.get("success_action") }}
                            </span>
                        </div>

                        <!-- Success Url -->
                        <div class="col-span-1 text-md text-gray-800 font-medium">
                            Success Url
                            <div class="text-sm text-gray-500 font-normal">
                                Provide a url where the reporter should be redirected after reporting an issue
                            </div>

                            <input type="text" class="form-control mt-2"
                                   v-model="form.success_url"
                                   :class="{'is-invalid': form.errors.has('success_url')}"></input>
                            <span class="invalid-feedback" v-show="form.errors.has('success_url')">
                                {{ form.errors.get("success_url") }}
                            </span>
                        </div>

                        <!-- Auto Email Reporter -->
                        <div class="col-span-1 text-md text-gray-800 font-medium my-auto">
                            <checkbox
                                @input="handleAutoEmailInput"
                                v-model="form.auto_email_reporter" label="Auto Email Reporter"
                                class="flex-grow-1 justify-content-start font-medium">
                            </checkbox>

                            <div class="text-sm text-gray-500 font-normal">
                                Automatically send out a chosen saved reply as an email to the reporter after reporting an issue
                            </div>
                        </div>

                        <div class="col-span-1">
                            <div v-show="form.auto_email_reporter">
                                <saved-reply-notification-input type="email" role="tenant" @input="clearErrors('tenant', 'email')"
                                                                :to-email="null" ref="autoEmailSavedReply" category="maintenance requests" disable-editing
                                                                v-model="form.auto_email_saved_reply">
                                </saved-reply-notification-input>

                                <div class="text-sm text-yellow-600 font-normal">
                                    Please note that only "Team" and "Maintenance Request" ##tags## will be replaced
                                </div>

                                <span class="text-red-500 text-sm mt-1 flex justify-end"
                                      v-if="form.errors.has(`auto_email_saved_reply.custom_body`)">
                                    {{ form.errors.get(`auto_email_saved_reply.custom_body`) }}
                                </span>
                            </div>
                        </div>

                        <!-- New Ordering -->
                        <div class="col-span-1 text-md text-gray-800 font-medium">
                            New Requests Ordering
                            <div class="text-sm text-gray-500 font-normal">
                                The direction new requests are ordered on the main list
                            </div>

                            <el-select
                                class="mt-2"
                                v-model="form.new_ordering"
                                placeholder="Direction">
                                <el-option v-for="(value, key) in orderingOptions" :key="key" :value="key"
                                           :label="value"></el-option>
                            </el-select>
                        </div>

                        <!-- Closed Ordering -->
                        <div class="col-span-1 text-md text-gray-800 font-medium">
                            Closed Requests Ordering
                            <div class="text-sm text-gray-500 font-normal">
                                The direction closed requests are ordered on the main list
                            </div>

                            <el-select
                                class="mt-2"
                                v-model="form.closed_ordering"
                                placeholder="Direction">
                                <el-option v-for="(value, key) in orderingOptions" :key="key" :value="key"
                                           :label="value"></el-option>
                            </el-select>
                        </div>
                    </div>

                    <div class="form-group w-full flex items-center justify-end mt-4 outline-none">
                        <secondary-button
                            @click.prevent="update()"
                            :disabled="form.busy">
                            {{ form.busy ? "Saving .." : "Save Settings" }}
                        </secondary-button>
                    </div>
                </div>
            </div>

            <div class="bg-white rounded-lg shadow-md p-8 pb-2">
                <!-- Customize quick selection issues-->
                <div class="flex flex-col gap-y-4 w-full">
                    <div class="flex flex-col gap-6">
                        <div class="text-lg text-gray-800 font-medium">
                            Quick Select Issues Management
                            <div class="text-sm text-gray-500 font-normal">
                                Manage and activate the quick select options for your clients based on your company type. Drag to re-order and click to edit.
                            </div>
                        </div>

                        <!-- Load all available grouped default maintenance request issue options -->
                        <div class="flex flex-col gap-y-6">
                            <maintenance-request-settings-common-issues-list v-for="(group, index) in availableMaintenanceRequestGroups"
                                                                             :key="index" class="flex flex-col gap-2"
                                                                             :company-type="group" :settings-id="form.id"
                                                                             @change-company-types="addOrRemoveMaintenanceRequestIssueOptionFromCurrentDefaults">
                            </maintenance-request-settings-common-issues-list>
                        </div>

                        <!-- Headers for the rest of the default options which are not currently on the team -->
                        <div class="flex flex-col gap-y-6">
                            <div v-for="maintenanceRequestIssueGroup in currentDefaultCompanyTypes" class="flex flex-col gap-2">
                                <!-- Group type header -->
                                <div class="flex items-center justify-between gap-x-6 mb-4">
                                    <span class="text-md text-gray-800 font-normal flex items-center gap-x-1">
                                        {{ maintenanceRequestIssueGroup | titlecase }} Issue Options
                                    </span>

                                    <div class="flex items-center gap-4">
                                        <secondary-button @click.prevent="toggleShowCreateMaintenanceRequestIssueModal(maintenanceRequestIssueGroup)">
                                            New Issue
                                        </secondary-button>

                                        <secondary-button @click.prevent="populateDefaults(maintenanceRequestIssueGroup)">
                                            Populate Defaults
                                        </secondary-button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div v-if="hasFeature('feat_maintenance_requests') && loading" class="flex justify-center items-center py-64">
            <icon-refresh class="h-10 w-10 text-indigo-600 animate-spin"/>
        </div>

        <div v-if="!hasFeature('feat_maintenance_requests')" class="p-12">
            <div>
                <div class="row page-titles">
                    <div class="col-12 align-self-center">
                        <h3 class="text-center text-3xl font-medium">Online Problem Reporting</h3>
                        <h4 class="mt-5 text-center text-2xl font-medium text-blue-500">Available Now For <span class="font-bold">Enterprise</span> Clients</h4>
                        <h6 class=" mt-5 text-center text-base font-normal text-gray-700 max-w-1/2 mt-2 leading-normal">
                            Organize your maintenance by having reporters
                            <br>report problems directly to your RedRabbit profile
                        </h6>
                    </div>
                </div>

                <div class="mt-3 flex justify-around m-auto items-center">
                    <div class="p-5 bg-white rounded-lg shadow-lg max-w-2xl">
                        <img alt="maintenance report example" src="/images/feat_maintenance.png"/>
                    </div>
                </div>
                <div class="text-center mt-6">
                    <h4 class="text-base font-medium"><a href="/settings#/tab-billing">Click here to upgrade now</a></h4>
                </div>
            </div>
        </div>

        <confirmation-dialog :show="showChangeSlugModal"
                             proceed-button-text="Update"
                             :proceed-disabled="subdomainCharCount === 63"
                             @cancelled="closeNewSubdomainModal"
                             @proceed="changeSlug"
                             title=""
                             proceed-button-colour="indigo-600"
                             proceed-button-hover-colour="indigo-400">
            <div slot="icon"></div>
            <div slot="header"></div>
            <div slot="title">
                <div class="flex justify-between pb-2">
                    <h1 class="text-2xl font-base">Change link</h1>
                </div>
            </div>
            <div slot="body">
                <div class="flex flex-col md:flex-row md:justify-between md:items-center">
                    <h2 class="text-base text-left">Enter new subdomain</h2>
                    <p class="flex items-center text-sm pr-1"
                       :class="subdomainCharCount < 0 ? 'text-red-600' : 'text-gray-400'">
                        {{ subdomainCharCount }}
                    </p>
                </div>

                <input v-model="mutableSubdomain" maxlength="63" @input="form.setErrors(null)"
                       class="border rounded focus:border-blue-500 hover:border-gray-400 w-full p-2"/>
                <span class="text-sm text-red-500" v-show="form.errors.has('slug')">
                    {{ form.errors.get("slug") }}
                </span>
            </div>
        </confirmation-dialog>

        <common-maintenance-request-issues-create-edit-modal :show="showCreateCommonMaintenanceRequestIssueModal"
                                                             :company-type="creatingForCompanyType" @added-issue="addMaintenanceRequestIssueToList"
                                                             @close="toggleShowCreateMaintenanceRequestIssueModal">
        </common-maintenance-request-issues-create-edit-modal>
    </div>
</template>

<script>
import MaintenanceRequestSettingsMixin from "../../mixins/maintenance-request-settings-mixin";
import CommonMaintenanceRequestIssuesCreateEditModal from "./CommonMaintenanceRequestIssuesCreateEditModal";

export default {
    name: "MaintenanceRequestSettingsForm",

    components: {CommonMaintenanceRequestIssuesCreateEditModal},

    mixins: [
        require("../../mixins/copy-to-clipboard").default,
        MaintenanceRequestSettingsMixin
    ],

    data () {
        return {
            form: new AppForm({}),
            loading: true,
            orderingOptions: {asc: "Ascending", desc: "Descending"},
            showChangeSlugModal: false,
            mutableSubdomain: "",
            creatingForCompanyType: "",
            showCreateCommonMaintenanceRequestIssueModal: false,
            availableMaintenanceRequestGroups: [],
            currentDefaultCompanyTypes: ["residential", "body corporate", "commercial"],
        };
    },

    mounted () {
        this.fetchSettings();
    },

    computed: {
        logoUploadUrl () {
            return "/settings/maintenance/requests/" + this.form.id + "/logo";
        },
        subdomainCharCount () {
            return 63 - this.mutableSubdomain.length;
        }
    },

    methods: {
        fetchSettings () {
            this.loading = true;
            axios.get("/settings/maintenance/requests")
            .then(response => {
                this.form = new AppForm(response.data);
                delete this.form.available_maintenance_request_groups;

                this.availableMaintenanceRequestGroups = response.data.available_maintenance_request_groups;
                this.availableMaintenanceRequestGroups.forEach(group => {
                    this.addOrRemoveMaintenanceRequestIssueOptionFromCurrentDefaults(group);
                });
                this.loading = false;
            }).catch(error => {
                this.loading = false;
                toastr.error(error.message);
            });
        },

        update () {
            App.patch(`/settings/maintenance/requests/${this.form.id}/update`, this.form)
            .then(response => toastr.success(response))
            .catch(error => toastr.error(error.message));
        },

        copyPageUrl () {
            this.copyToClipboard(this.form.public_page_url);
            toastr.success("Link Copied!");
        },

        changeSlug () {
            App.patch(`/settings/maintenance/requests/${this.form.id}/change-slug`, new AppForm({slug: this.mutableSubdomain}))
            .then(response => {
                this.form.public_page_url = response;
                toastr.success("Subdomain successfully updated");
                this.closeNewSubdomainModal();
            })
            .catch(error => {
                this.form.setErrors(error.errors);
                toastr.error(error.message);
            });
        },

        closeNewSubdomainModal () {
            this.showChangeSlugModal = false;
            this.mutableSubdomain = "";
        },

        populateDefaults (companyType) {
            this.resetMaintenanceRequestGroupToDefaults(this.form.id, companyType)
            .then(() => {
                return new Promise((resolve) => {
                    this.availableMaintenanceRequestGroups.push(companyType);
                    resolve();
                });
            })
            .then(() => this.addOrRemoveMaintenanceRequestIssueOptionFromCurrentDefaults(companyType));
        },

        addOrRemoveMaintenanceRequestIssueOptionFromCurrentDefaults (companyType) {
            return new Promise((resolve) => {
                // Remove types which are not yet populated on the team
                let defaultIndex = this.currentDefaultCompanyTypes.findIndex(type => type === companyType);

                if (defaultIndex !== -1) {
                    this.currentDefaultCompanyTypes.splice(defaultIndex, 1);
                } else {
                    this.currentDefaultCompanyTypes.push(companyType);

                    let availableIndex = this.availableMaintenanceRequestGroups.findIndex(type => type === companyType);
                    if (availableIndex !== -1) {
                        this.availableMaintenanceRequestGroups.splice(availableIndex, 1);
                    }
                }
                resolve();
            });
        },

        toggleShowCreateMaintenanceRequestIssueModal (companyType = "") {
            this.creatingForCompanyType = companyType;
            this.showCreateCommonMaintenanceRequestIssueModal = !this.showCreateCommonMaintenanceRequestIssueModal;
        },

        addMaintenanceRequestIssueToList (issue) {
            this.addOrRemoveMaintenanceRequestIssueOptionFromCurrentDefaults(issue.for_company_type);
            this.availableMaintenanceRequestGroups.push(issue.for_company_type);
        },

        clearErrors (role, commType) {
            if (this.form.errors.has(`${role}_${commType}_communication.custom_body`)) {
                this.form.errors.forget(`${role}_${commType}_communication.custom_body`);
            }
        },

        handleAutoEmailInput (value) {
            if (value && this.$refs.autoEmailSavedReply) {
                this.$refs.autoEmailSavedReply.setIsSelected(true);
            } else {
                this.form.auto_email_saved_reply = null;
            }
        },
    },
};
</script>
